import { useCallback, useEffect, useMemo, useState } from "react";
import ActionButton from "@/components/ActionButton";
import { Link } from "react-router-dom";
import api, { formatEur, formatDate } from "@/lib/api";
import { useBrand } from "@/contexts/BrandContext";
import { Plus, Search, Upload, Trash2, AlertCircle } from "lucide-react";
import ProjectImportModal from "@/components/ProjectImportModal";
import EnseigneSelector from "@/components/EnseigneSelector";
import FranchiseurSelector from "@/components/FranchiseurSelector";

const STATUS_LABEL = {
  a_demarrer: "À démarrer",
  en_cours: "En cours",
  en_attente_client: "Attente client",
  en_attente_mairie: "Attente mairie",
  livre: "Livré",
  abandonne: "Abandonné",
};

const STATUS_COLOR = {
  a_demarrer: "bg-stone-100 text-stone-700",
  en_cours: "bg-emerald-50 text-emerald-800",
  en_attente_client: "bg-amber-50 text-amber-800",
  en_attente_mairie: "bg-amber-50 text-amber-800",
  livre: "bg-blue-50 text-blue-800",
  abandonne: "bg-rose-50 text-rose-800 line-through",
};

// Priority order for the default "En cours" tab
const STATUS_PRIORITY = [
  "_retard",
  "a_demarrer",
  "en_cours",
  "en_attente_client",
  "en_attente_mairie",
];

const TABS = [
  { id: "en_cours", l: "En cours", filter: (p) => ["en_cours", "en_attente_client", "en_attente_mairie"].includes(p.status) },
  { id: "a_demarrer", l: "À démarrer", filter: (p) => p.status === "a_demarrer" },
  { id: "en_attente", l: "En attente", filter: (p) => ["en_attente_client", "en_attente_mairie"].includes(p.status) },
  { id: "livres", l: "Livrés", filter: (p) => p.status === "livre" },
  { id: "abandonnes", l: "Abandonnés", filter: (p) => p.status === "abandonne" },
  { id: "tous", l: "Tous", filter: () => true },
];

export default function Projects() {
  const { brand } = useBrand();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("en_cours");
  const [show, setShow] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [franchiseur, setFranchiseur] = useState("");
  const [groupByFr, setGroupByFr] = useState(false);
  const [formEnseigne, setFormEnseigne] = useState("");
  const [formFranchiseur, setFormFranchiseur] = useState("");

  const load = useCallback(async () => {
    const [{ data: p }, { data: s }] = await Promise.all([
      api.get("/projects"),
      api.get("/projects/stats"),
    ]);
    setProjects(p); setStats(s);
  }, []);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    await api.put(`/projects/${id}`, { status });
    load();
  };

  const doDelete = async (id, force = false) => {
    try {
      await api.delete(`/projects/${id}`, { params: force ? { force: true } : {} });
      setConfirmDel(null);
      load();
    } catch (e) {
      if (e.response?.status === 409) {
        const deps = e.response.data?.detail?.dependencies || {};
        const parts = [];
        if (deps.demarches) parts.push(`${deps.demarches} démarche(s)`);
        if (deps.finance_docs) parts.push(`${deps.finance_docs} document(s) finance`);
        if (deps.time_entries) parts.push(`${deps.time_entries} saisie(s) temps`);
        if (deps.drive_folders) parts.push(`${deps.drive_folders} dossier(s) Drive`);
        if (deps.ideas) parts.push(`${deps.ideas} idée(s) LinkedIn`);
        if (deps.contacts) parts.push(`${deps.contacts} contact(s) lié(s)`);
        const ok = window.confirm(`Ce projet est lié à : ${parts.join(", ")}.\n\nLe supprimer effacera également ces éléments rattachés.\nConfirmer la suppression définitive ?`);
        if (ok) await doDelete(id, true);
      } else {
        alert("Erreur : " + (e.response?.data?.detail || e.message));
      }
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  const enrich = (p) => {
    const isRetard = p.date_objectif && p.date_objectif < today && !["livre", "abandonne", "en_attente_mairie", "en_attente_client"].includes(p.status);
    return { ...p, _retard: isRetard };
  };

  const filtered = useMemo(() => {
    const t = TABS.find((x) => x.id === tab) || TABS[0];
    let list = projects.map(enrich).filter(t.filter);
    if (brand !== "ALL") list = list.filter((p) => p.brand === brand);
    if (franchiseur) list = list.filter((p) => (p.franchiseur || "Indépendant") === franchiseur);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((p) =>
        [p.nom, p.client, p.enseigne, p.ville, p.franchiseur, p.contact_principal]
          .filter(Boolean).some((v) => v.toLowerCase().includes(s))
      );
    }
    list.sort((a, b) => {
      if (a._retard && !b._retard) return -1;
      if (b._retard && !a._retard) return 1;
      const ai = STATUS_PRIORITY.indexOf(a.status);
      const bi = STATUS_PRIORITY.indexOf(b.status);
      if (ai !== bi) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      return (a.date_objectif || "9999") < (b.date_objectif || "9999") ? -1 : 1;
    });
    return list;
  }, [projects, tab, brand, search, franchiseur]);

  const franchiseurOptions = useMemo(() => {
    const set = new Map();
    projects.forEach((p) => {
      const fr = (p.franchiseur || "").trim() || "Indépendant";
      set.set(fr, (set.get(fr) || 0) + 1);
    });
    return Array.from(set.entries()).sort((a, b) => b[1] - a[1]);
  }, [projects]);

  const groupedByFranchiseur = useMemo(() => {
    if (!groupByFr) return null;
    const groups = {};
    filtered.forEach((p) => {
      const fr = (p.franchiseur || "").trim() || "Indépendant";
      (groups[fr] = groups[fr] || []).push(p);
    });
    return Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
  }, [filtered, groupByFr]);

  const create = async (e) => {
    e.preventDefault();
    const form = e.currentTarget.closest("form");
    const fd = new FormData(form);
    const payload = Object.fromEntries(fd.entries());
    payload.enseigne = formEnseigne;
    payload.franchiseur = formFranchiseur;
    payload.montant_vendu = parseFloat(payload.montant_vendu || 0);
    payload.temps_prevu_h = parseFloat(payload.temps_prevu_h || 0);
    payload.demarches_requises = fd.get("demarches_requises") === "on";
    await api.post("/projects", payload);
    setShow(false);
    setFormEnseigne(""); setFormFranchiseur("");
    load();
  };

  const tabCount = (t) => projects.map(enrich).filter(t.filter).length;

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Pilotage</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Projets</h1>
          <p className="text-sm text-stone-500 mt-1">Vue par défaut : projets actifs · retard prioritaire.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="project-new-button" onClick={() => { setFormEnseigne(""); setFormFranchiseur(""); setShow(true); }} className="bg-stone-900 hover:bg-black text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
            <Plus size={16} /> Nouveau projet
          </button>
          <button data-testid="project-import-button" onClick={() => setShowImport(true)} className="bg-white hover:bg-stone-50 text-stone-900 border border-stone-200 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
            <Upload size={16} /> Import historique
          </button>
        </div>
      </div>

      {/* KPI block */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="projects-kpi">
          <Kpi label="Projets actifs" value={stats.projets_actifs} testid="kpi-actifs"/>
          <Kpi label="Projets terminés" value={stats.projets_termines} testid="kpi-termines"/>
          <Kpi label="CA en cours (HT)" value={formatEur(stats.ca_en_cours)} mono testid="kpi-ca-cours"/>
          <Kpi label="CA réalisé (HT)" value={formatEur(stats.ca_realise)} mono accent="emerald" testid="kpi-ca-realise"/>
          <Kpi label="Temps vendu" value={`${stats.temps_vendu_h} h`} mono testid="kpi-tps-vendu"/>
          <Kpi label="Temps consommé" value={`${stats.temps_consomme_h} h`} mono testid="kpi-tps-conso"/>
          <Kpi
            label="Marge réelle %"
            value={(() => {
              const ca = Number(stats.ca_en_cours) || 0;
              const m = Number(stats.marge_reelle) || 0;
              const pct = ca > 0 ? Math.round((m / ca) * 100) : 0;
              return `${pct} %`;
            })()}
            subtitle={formatEur(stats.marge_reelle)}
            mono accent="emerald" testid="kpi-marge-reelle-pct"
          />
        </div>
      )}

      {importResult && (
        <div className="text-sm rounded px-3 py-2 border bg-emerald-50 border-emerald-200 text-emerald-900" data-testid="import-result-banner">
          Import terminé · <strong>{importResult.created ?? importResult.projects_created ?? 0}</strong> créé(s) · <strong>{importResult.merged ?? importResult.projects_merged ?? 0}</strong> fusionné(s) · <strong>{importResult.finance_attached ?? importResult.finance_created ?? 0}</strong> docs · <strong>{importResult.avoirs_created ?? 0}</strong> avoir(s) · <strong>{importResult.skipped ?? 0}</strong> ignoré(s)
        </div>
      )}

      {showImport && (
        <ProjectImportModal
          onClose={() => setShowImport(false)}
          onDone={(r) => { setShowImport(false); setImportResult(r); load(); }}
        />
      )}

      {/* Tabs + search */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200">
        <div className="flex flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-sm font-semibold border-b-2 transition flex items-center gap-2 ${tab === t.id ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-800"}`}
            >
              {t.l}
              <span className={`pill text-[10px] ${tab === t.id ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>{tabCount(t)}</span>
            </button>
          ))}
        </div>
        <div className="relative max-w-md flex-1 pb-2">
          <Search size={16} className="absolute left-3 top-2.5 text-stone-400" />
          <input data-testid="project-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher : nom, enseigne, client, ville, franchiseur…" className="w-full pl-9 pr-3 py-2 border border-stone-200 rounded-lg text-sm bg-white" />
        </div>
      </div>

      {/* Franchiseur filter + group toggle */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Franchiseur :</span>
        <button data-testid="fr-filter-all" onClick={() => setFranchiseur("")} className={`px-2 py-1 rounded ${!franchiseur ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700"}`}>Tous</button>
        {franchiseurOptions.slice(0, 8).map(([fr, count]) => (
          <button key={fr} data-testid={`fr-filter-${fr}`} onClick={() => setFranchiseur(franchiseur === fr ? "" : fr)} className={`px-2 py-1 rounded font-semibold ${franchiseur === fr ? "bg-[var(--ppd)] text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}>
            {fr} <span className="opacity-60">({count})</span>
          </button>
        ))}
        <button data-testid="fr-group-toggle" onClick={() => setGroupByFr((g) => !g)} className={`ml-auto px-3 py-1 rounded border ${groupByFr ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-200 text-stone-700"}`}>
          {groupByFr ? "✓ Groupé par franchiseur" : "Grouper par franchiseur"}
        </button>
        <Link to="/franchiseurs" className="text-stone-700 hover:text-stone-900 underline">Vue franchiseurs →</Link>
      </div>

      {/* Table or grouped view */}
      {groupedByFranchiseur ? (
        <div className="space-y-4" data-testid="grouped-view">
          {groupedByFranchiseur.map(([fr, items]) => (
            <div key={fr} className="card-flat overflow-hidden">
              <div className="bg-stone-50 px-4 py-3 border-b border-stone-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-display font-bold text-base">{fr}</span>
                  <span className="pill bg-stone-900 text-white text-[10px]">{items.length}</span>
                </div>
                <div className="text-xs font-mono text-stone-600">
                  {formatEur(items.reduce((s, p) => s + (p.montant_vendu || 0), 0))} vendus
                </div>
              </div>
              <ProjectsTable items={items} STATUS_LABEL={STATUS_LABEL} STATUS_COLOR={STATUS_COLOR} confirmDel={confirmDel} setConfirmDel={setConfirmDel} doDelete={doDelete} onStatus={changeStatus}/>
            </div>
          ))}
        </div>
      ) : (
        <div className="card-flat overflow-hidden">
          <ProjectsTable items={filtered} STATUS_LABEL={STATUS_LABEL} STATUS_COLOR={STATUS_COLOR} confirmDel={confirmDel} setConfirmDel={setConfirmDel} doDelete={doDelete} onStatus={changeStatus}/>
        </div>
      )}

      {show && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <form onSubmit={create} className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto my-auto" data-testid="project-form">
            <h3 className="font-display text-2xl font-bold mb-4">Nouveau projet</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                ["nom", "Nom du projet *", true],
                ["client", "Client *", true],
                ["ville", "Ville", false],
                ["adresse", "Adresse", false],
                ["contact_principal", "Contact principal", false],
                ["telephone", "Téléphone", false],
                ["email", "Email", false],
              ].map(([n, l, req]) => (
                <label key={n} className="flex flex-col">
                  <span className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">{l}</span>
                  <input name={n} required={req} className="border border-stone-200 rounded px-3 py-2" />
                </label>
              ))}
              <label className="flex flex-col">
                <span className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">Enseigne</span>
                <EnseigneSelector value={formEnseigne} testid="project-enseigne-selector"
                  onSave={(nom, fr) => { setFormEnseigne(nom); if (fr) setFormFranchiseur(fr); }} />
              </label>
              <label className="flex flex-col">
                <span className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">Franchiseur</span>
                <FranchiseurSelector value={formFranchiseur} testid="project-franchiseur-selector"
                  onSave={(nom) => setFormFranchiseur(nom)} />
              </label>
              <label className="flex flex-col col-span-2">
                <span className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">Type de projet *</span>
                <select name="type_projet" required className="border border-stone-200 rounded px-3 py-2">
                  <option value="concept_franchise">Concept franchise complet</option>
                  <option value="projet_simple">Projet simple / partiel</option>
                  <option value="franchimob">Franchi'Mob</option>
                  <option value="administratif">Administratif uniquement</option>
                  <option value="mission_ponctuelle">Mission ponctuelle</option>
                </select>
              </label>
              <label className="flex flex-col">
                <span className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">Marque</span>
                <select name="brand" className="border border-stone-200 rounded px-3 py-2"><option>PPD</option><option>FRANCHIMOB</option></select>
              </label>
              <label className="flex flex-col">
                <span className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">Prestation</span>
                <input name="prestation" className="border border-stone-200 rounded px-3 py-2" placeholder="Esquisse, complet…" />
              </label>
              <label className="flex flex-col"><span className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">Date début</span><input type="date" name="date_debut" className="border border-stone-200 rounded px-3 py-2" /></label>
              <label className="flex flex-col"><span className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">Date objectif</span><input type="date" name="date_objectif" className="border border-stone-200 rounded px-3 py-2" /></label>
              <label className="flex flex-col"><span className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">Montant vendu (€)</span><input type="number" name="montant_vendu" className="border border-stone-200 rounded px-3 py-2" /></label>
              <label className="flex flex-col"><span className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">Temps prévu (h)</span><input type="number" name="temps_prevu_h" className="border border-stone-200 rounded px-3 py-2" /></label>
            </div>
            <label className="flex items-center gap-2 mt-3 text-sm font-semibold text-stone-700 bg-stone-50 border border-stone-100 rounded p-2.5 cursor-pointer">
              <input type="checkbox" name="demarches_requises" data-testid="project-demarches-requises" className="accent-[var(--ppd)] w-4 h-4" />
              Démarches administratives à déposer pour ce projet
            </label>
            <div className="flex gap-2 mt-5 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm border border-stone-200 rounded">Annuler</button>
              <ActionButton data-testid="project-submit-button" type="button" onClick={create} successLabel="Créé" className="px-4 py-2 text-sm bg-stone-900 text-white rounded font-semibold">Créer</ActionButton>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, subtitle, mono, accent, testid }) {
  const cls = accent === "emerald" ? "text-emerald-700" : "text-stone-900";
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">{label}</div>
      <div className={`${mono ? "font-mono" : ""} text-lg font-bold ${cls}`}>{value}</div>
      {subtitle && <div className={`text-[10px] ${mono ? "font-mono" : ""} text-stone-500 mt-0.5`}>{subtitle}</div>}
    </div>
  );
}

function ProjectsTable({ items, STATUS_LABEL, STATUS_COLOR, confirmDel, setConfirmDel, doDelete, onStatus }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-stone-50 text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200">
        <tr>
          <th className="px-4 py-3 text-left font-semibold">Projet</th>
          <th className="px-4 py-3 text-left font-semibold">Client</th>
          <th className="px-4 py-3 text-left font-semibold">Franchiseur</th>
          <th className="px-4 py-3 text-left font-semibold">Statut</th>
          <th className="px-4 py-3 text-right font-semibold">CA HT</th>
          <th className="px-4 py-3 text-left font-semibold">Échéance</th>
          <th className="px-4 py-3 text-right font-semibold w-12"></th>
        </tr>
      </thead>
      <tbody>
        {items.map((p) => (
          <tr key={p.id} className="border-b border-stone-100 hover:bg-stone-50">
            <td className="px-4 py-3">
              <Link data-testid={`project-row-${p.id}`} to={`/projets/${p.id}`} className="font-semibold text-stone-900 hover:underline">{p.nom}</Link>
              <div className="text-xs text-stone-500">{p.enseigne || "—"}{p.ville ? ` · ${p.ville}` : ""}</div>
            </td>
            <td className="px-4 py-3">{p.client}</td>
            <td className="px-4 py-3 text-stone-700">{p.franchiseur || "Indépendant"}</td>
            <td className="px-4 py-3">
              <select
                data-testid={`project-status-select-${p.id}`}
                value={p.status}
                onChange={(e) => onStatus(p.id, e.target.value)}
                className={`pill border-0 outline-none cursor-pointer appearance-none pr-5 ${STATUS_COLOR[p.status] || "bg-stone-100 text-stone-700"}`}
                title="Changer le statut"
              >
                {Object.entries(STATUS_LABEL).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
              {p._retard && <span className="pill bg-stone-900 text-white ml-1 text-[10px]"><AlertCircle size={9} className="inline mr-0.5 -mt-0.5"/>retard</span>}
              {p.has_unpaid_facture && <span className="pill bg-rose-50 text-rose-700 ml-1 text-[10px]" data-testid={`project-impaye-${p.id}`}>Impayé</span>}
            </td>
            <td className="px-4 py-3 text-right font-mono">{formatEur(p.montant_vendu)}</td>
            <td className="px-4 py-3 text-stone-600">{formatDate(p.date_objectif)}</td>
            <td className="px-4 py-3 text-right">
              {confirmDel === p.id ? (
                <span className="text-xs">
                  <span className="text-stone-700 font-semibold">Sûr ?</span>
                  <button onClick={() => doDelete(p.id)} data-testid={`project-del-confirm-${p.id}`} className="ml-2 text-stone-900 font-semibold underline">Oui</button>
                  <button onClick={() => setConfirmDel(null)} className="ml-1 text-stone-500 underline">Non</button>
                </span>
              ) : (
                <button onClick={() => setConfirmDel(p.id)} data-testid={`project-del-${p.id}`} className="text-stone-400 hover:text-stone-900"><Trash2 size={14}/></button>
              )}
            </td>
          </tr>
        ))}
        {items.length === 0 && <tr><td colSpan={7} className="text-center text-stone-400 py-12">Aucun projet</td></tr>}
      </tbody>
    </table>
  );
}
